import type { Offering, ProductOffering } from "../content/offerings/types";
import { SITE_CONTACT } from "./siteContact";
import { SITE_DEFAULTS, absoluteImageUrl, absoluteUrl } from "./siteSeo";
import type { FaqArticle } from "./faq";
import type { Project } from "./projects";
import { stripHtml } from "./projects";
import { getProjectUrl } from "./portfolioRoutes";
import type { WatchVideo } from "./watchVideos";
import { getWatchPath } from "./watchVideos";

export type JsonLd = Record<string, unknown>;

const ORG_ID = `${SITE_DEFAULTS.url}/#organization`;
const PERSON_ID = `${SITE_DEFAULTS.url}/#person`;
const WEBSITE_ID = `${SITE_DEFAULTS.url}/#website`;

function stripUndefined(obj: JsonLd): JsonLd {
  const out: JsonLd = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      const cleaned = value.filter((v) => v !== undefined && v !== null && v !== "");
      if (cleaned.length) out[key] = cleaned;
      continue;
    }
    if (typeof value === "object") {
      out[key] = stripUndefined(value as JsonLd);
      continue;
    }
    out[key] = value;
  }
  return out;
}

/** Site-wide graph for home / index.html (Organization + Person + WebSite). */
export function siteGraphJsonLd(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@graph": [
      stripUndefined({
        "@type": ["Organization", "ProfessionalService"],
        "@id": ORG_ID,
        name: "UXMichal",
        alternateName: ["UX Michal", "UXMichal.com"],
        url: absoluteUrl("/"),
        logo: absoluteImageUrl(SITE_DEFAULTS.logo),
        image: absoluteImageUrl(SITE_DEFAULTS.image),
        description: SITE_DEFAULTS.description,
        email: SITE_CONTACT.email || undefined,
        founder: { "@id": PERSON_ID },
        employee: { "@id": PERSON_ID },
        areaServed: ["EU", "GB"],
        sameAs: [...SITE_DEFAULTS.sameAs],
      }),
      stripUndefined({
        "@type": "Person",
        "@id": PERSON_ID,
        name: SITE_DEFAULTS.personName,
        url: absoluteUrl("/"),
        image: absoluteImageUrl(SITE_DEFAULTS.logo),
        jobTitle: SITE_DEFAULTS.jobTitle,
        description: SITE_DEFAULTS.description,
        worksFor: { "@id": ORG_ID },
        sameAs: [...SITE_DEFAULTS.sameAs],
        email: SITE_CONTACT.email || undefined,
      }),
      stripUndefined({
        "@type": "WebSite",
        "@id": WEBSITE_ID,
        name: SITE_DEFAULTS.brandPrefix,
        url: absoluteUrl("/"),
        description: SITE_DEFAULTS.description,
        publisher: { "@id": ORG_ID },
        inLanguage: "en",
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: `${absoluteUrl("/search")}?q={search_term_string}`,
          },
          "query-input": "required name=search_term_string",
        },
      }),
    ],
  };
}

export function webPageJsonLd(input: {
  path: string;
  title: string;
  description: string;
  type?: string;
}): JsonLd {
  return stripUndefined({
    "@context": "https://schema.org",
    "@type": input.type ?? "WebPage",
    "@id": `${absoluteUrl(input.path)}#webpage`,
    url: absoluteUrl(input.path),
    name: input.title,
    description: input.description,
    isPartOf: { "@id": WEBSITE_ID },
    about: { "@id": ORG_ID },
    author: { "@id": PERSON_ID },
  });
}

/** Parse "From €550" / "€36 / hour" into Offer fields when possible. */
function offerFromPriceLabel(priceLabel: string): JsonLd | undefined {
  const cleaned = priceLabel.replace(/,/g, "").trim();
  if (/on request|project quote/i.test(cleaned)) {
    return {
      "@type": "Offer",
      availability: "https://schema.org/InStock",
      priceCurrency: "EUR",
      description: cleaned,
    };
  }
  const match = cleaned.match(/€\s*([\d]+(?:\.\d+)?)/);
  if (!match) {
    return {
      "@type": "Offer",
      availability: "https://schema.org/InStock",
      priceCurrency: "EUR",
      description: cleaned,
    };
  }
  return {
    "@type": "Offer",
    price: match[1],
    priceCurrency: "EUR",
    availability: "https://schema.org/InStock",
    url: absoluteUrl("/start-project"),
  };
}

export function productJsonLd(product: ProductOffering, path: string): JsonLd {
  return stripUndefined({
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.title,
    description: product.description,
    image: absoluteImageUrl(product.illustration),
    brand: { "@id": ORG_ID },
    url: absoluteUrl(path),
    offers: offerFromPriceLabel(product.priceFromLabel || product.price),
  });
}

export function serviceJsonLd(offering: Offering, path: string): JsonLd {
  return stripUndefined({
    "@context": "https://schema.org",
    "@type": "Service",
    name: offering.title,
    description: offering.description,
    provider: { "@id": ORG_ID },
    url: absoluteUrl(path),
    areaServed: ["EU", "GB"],
    offers: offerFromPriceLabel(offering.price),
  });
}

export function creativeWorkJsonLd(project: Project): JsonLd {
  const path = getProjectUrl(project);
  return stripUndefined({
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: stripHtml(project.title),
    description: stripHtml(project.description),
    image: absoluteImageUrl(project.hero || project.image),
    url: absoluteUrl(path),
    datePublished: project.date.toISOString(),
    author: { "@id": PERSON_ID },
    creator: { "@id": PERSON_ID },
    publisher: { "@id": ORG_ID },
    about: project.client || undefined,
  });
}

export function faqPageJsonLd(article: FaqArticle, path: string): JsonLd {
  return stripUndefined({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: article.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: stripHtml(article.contentHtml).slice(0, 5000),
        },
      },
    ],
    url: absoluteUrl(path),
  });
}

/** Watch-page VideoObject — required shape for Google video indexing. */
export function videoObjectJsonLd(video: WatchVideo): JsonLd {
  const path = getWatchPath(video.id);
  const embedUrl = `https://www.youtube.com/embed/${video.youtubeId}`;
  const contentUrl = `https://www.youtube.com/watch?v=${video.youtubeId}`;
  return stripUndefined({
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: video.title,
    description: video.description,
    thumbnailUrl: [absoluteImageUrl(video.thumbnail)],
    uploadDate: video.uploadDate,
    embedUrl,
    contentUrl,
    url: absoluteUrl(path),
    publisher: { "@id": ORG_ID },
    creator: { "@id": PERSON_ID },
  });
}

/** Serialize for <script type="application/ld+json"> (safe for HTML embedding). */
export function serializeJsonLd(data: JsonLd | JsonLd[]): string {
  return JSON.stringify(data).replace(/</g, "\\u003c");
}
